// import { UserRole } from '@aalaw/shared';

// Define UserRole locally to avoid import issues during build
export enum UserRole {
  CLIENT = 'CLIENT',
  PROSPECT = 'PROSPECT',
  ATTORNEY = 'ATTORNEY',
  CASE_MANAGER = 'CASE_MANAGER',
  OFFICE_MANAGER = 'OFFICE_MANAGER',
  INQUIRY_MANAGER = 'INQUIRY_MANAGER',
  LEAD_CASE_MANAGER = 'LEAD_CASE_MANAGER',
  MEDICAL_RECORDS_COORDINATOR = 'MEDICAL_RECORDS_COORDINATOR',
  PARALEGAL = 'PARALEGAL',
  ADMIN = 'ADMIN',
}

// Staff roles array for authorization checks
export const STAFF_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ATTORNEY,
  UserRole.CASE_MANAGER,
  UserRole.OFFICE_MANAGER,
  UserRole.INQUIRY_MANAGER,
  UserRole.LEAD_CASE_MANAGER,
  UserRole.MEDICAL_RECORDS_COORDINATOR,
  UserRole.PARALEGAL,
];

// Human-readable labels for roles
const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.CLIENT]: 'Client',
  [UserRole.PROSPECT]: 'Prospect',
  [UserRole.ATTORNEY]: 'Attorney',
  [UserRole.CASE_MANAGER]: 'Case Manager',
  [UserRole.OFFICE_MANAGER]: 'Office Manager',
  [UserRole.INQUIRY_MANAGER]: 'Inquiry Manager',
  [UserRole.LEAD_CASE_MANAGER]: 'Lead Case Manager',
  [UserRole.MEDICAL_RECORDS_COORDINATOR]: 'Medical Records Coordinator',
  [UserRole.PARALEGAL]: 'Paralegal',
  [UserRole.ADMIN]: 'Admin',
};

/**
 * Get human-readable label for a role
 */
export function getRoleLabel(role: UserRole | string | undefined): string {
  if (!role) return '';
  return ROLE_LABELS[role as UserRole] || role;
}

export interface Permission {
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'manage';
}

export const PERMISSIONS = {
  // User Management
  USERS_CREATE: { resource: 'users', action: 'create' as const },
  USERS_READ: { resource: 'users', action: 'read' as const },
  USERS_UPDATE: { resource: 'users', action: 'update' as const },
  USERS_DELETE: { resource: 'users', action: 'delete' as const },
  USERS_MANAGE: { resource: 'users', action: 'manage' as const },

  // Case Management
  CASES_CREATE: { resource: 'cases', action: 'create' as const },
  CASES_READ: { resource: 'cases', action: 'read' as const },
  CASES_UPDATE: { resource: 'cases', action: 'update' as const },
  CASES_DELETE: { resource: 'cases', action: 'delete' as const },
  CASES_MANAGE: { resource: 'cases', action: 'manage' as const },

  // Client Management
  CLIENTS_CREATE: { resource: 'clients', action: 'create' as const },
  CLIENTS_READ: { resource: 'clients', action: 'read' as const },
  CLIENTS_UPDATE: { resource: 'clients', action: 'update' as const },
  CLIENTS_DELETE: { resource: 'clients', action: 'delete' as const },
  CLIENTS_MANAGE: { resource: 'clients', action: 'manage' as const },

  // Inquiry Management
  INQUIRIES_CREATE: { resource: 'inquiries', action: 'create' as const },
  INQUIRIES_READ: { resource: 'inquiries', action: 'read' as const },
  INQUIRIES_UPDATE: { resource: 'inquiries', action: 'update' as const },
  INQUIRIES_DELETE: { resource: 'inquiries', action: 'delete' as const },
  INQUIRIES_MANAGE: { resource: 'inquiries', action: 'manage' as const },

  // Document Management
  DOCUMENTS_CREATE: { resource: 'documents', action: 'create' as const },
  DOCUMENTS_READ: { resource: 'documents', action: 'read' as const },
  DOCUMENTS_UPDATE: { resource: 'documents', action: 'update' as const },
  DOCUMENTS_DELETE: { resource: 'documents', action: 'delete' as const },
  DOCUMENTS_MANAGE: { resource: 'documents', action: 'manage' as const },

  // System Administration
  SYSTEM_SETTINGS: { resource: 'system', action: 'manage' as const },
  AUDIT_LOGS: { resource: 'audit', action: 'read' as const },
  REPORTS_ADVANCED: { resource: 'reports', action: 'manage' as const },
  BULK_OPERATIONS: { resource: 'bulk', action: 'manage' as const },
} as const;

// Role-based permissions mapping
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: [
    // Admins have all permissions
    PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.CASES_MANAGE,
    PERMISSIONS.CLIENTS_MANAGE,
    PERMISSIONS.INQUIRIES_MANAGE,
    PERMISSIONS.DOCUMENTS_MANAGE,
    PERMISSIONS.SYSTEM_SETTINGS,
    PERMISSIONS.AUDIT_LOGS,
    PERMISSIONS.REPORTS_ADVANCED,
    PERMISSIONS.BULK_OPERATIONS,
  ],
  [UserRole.ATTORNEY]: [
    // Attorneys can manage their assigned cases and clients
    PERMISSIONS.CASES_READ,
    PERMISSIONS.CASES_UPDATE,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.CLIENTS_UPDATE,
    PERMISSIONS.INQUIRIES_READ,
    PERMISSIONS.INQUIRIES_UPDATE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_CREATE,
    PERMISSIONS.DOCUMENTS_UPDATE,
  ],
  [UserRole.CASE_MANAGER]: [
    // Case managers have broader access but can't delete
    PERMISSIONS.CASES_READ,
    PERMISSIONS.CASES_UPDATE,
    PERMISSIONS.CASES_CREATE,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.CLIENTS_UPDATE,
    PERMISSIONS.CLIENTS_CREATE,
    PERMISSIONS.INQUIRIES_READ,
    PERMISSIONS.INQUIRIES_UPDATE,
    PERMISSIONS.INQUIRIES_CREATE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_CREATE,
    PERMISSIONS.DOCUMENTS_UPDATE,
  ],
  [UserRole.OFFICE_MANAGER]: [
    // Office managers handle user management, case reading, client management, and inquiry management
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_UPDATE,
    PERMISSIONS.CASES_READ,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.CLIENTS_CREATE,
    PERMISSIONS.CLIENTS_UPDATE,
    PERMISSIONS.INQUIRIES_READ,
    PERMISSIONS.INQUIRIES_UPDATE,
    PERMISSIONS.INQUIRIES_CREATE,
    PERMISSIONS.DOCUMENTS_READ,
  ],
  [UserRole.INQUIRY_MANAGER]: [
    // Inquiry managers handle inquiry triage and management
    PERMISSIONS.INQUIRIES_MANAGE,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.CASES_READ,
    PERMISSIONS.DOCUMENTS_READ,
  ],
  [UserRole.LEAD_CASE_MANAGER]: [
    // Lead case managers have all CASE_MANAGER permissions plus supervisory access
    PERMISSIONS.CASES_READ,
    PERMISSIONS.CASES_UPDATE,
    PERMISSIONS.CASES_CREATE,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.CLIENTS_UPDATE,
    PERMISSIONS.CLIENTS_CREATE,
    PERMISSIONS.INQUIRIES_READ,
    PERMISSIONS.INQUIRIES_UPDATE,
    PERMISSIONS.INQUIRIES_CREATE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_CREATE,
    PERMISSIONS.DOCUMENTS_UPDATE,
    PERMISSIONS.USERS_READ, // Supervisory: view staff
  ],
  [UserRole.MEDICAL_RECORDS_COORDINATOR]: [
    // Medical records coordinators manage documents and have read access to cases/clients
    PERMISSIONS.DOCUMENTS_MANAGE,
    PERMISSIONS.CASES_READ,
    PERMISSIONS.CLIENTS_READ,
  ],
  [UserRole.PARALEGAL]: [
    // Paralegals handle document management, case reading, client reading, and inquiry reading
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_CREATE,
    PERMISSIONS.DOCUMENTS_UPDATE,
    PERMISSIONS.CASES_READ,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.INQUIRIES_READ,
  ],
  [UserRole.CLIENT]: [
    // Clients can only read their own data
    PERMISSIONS.CASES_READ,
    PERMISSIONS.DOCUMENTS_READ,
  ],
  [UserRole.PROSPECT]: [
    // Prospects have minimal access
    PERMISSIONS.DOCUMENTS_READ,
  ],
};

/**
 * Check if a user has a specific permission
 */
export function hasPermission(userRole: UserRole | string | undefined, permission: Permission): boolean {
  if (!userRole) return false;
  const role = userRole as UserRole;
  const rolePermissions = ROLE_PERMISSIONS[role] || [];
  
  return rolePermissions.some(p => 
    p.resource === permission.resource && 
    (p.action === permission.action || p.action === 'manage')
  );
}

/**
 * Check if user can perform action on resource
 */
export function canPerformAction(
  userRole: UserRole | string | undefined, 
  resource: string, 
  action: Permission['action']
): boolean {
  return hasPermission(userRole, { resource, action });
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(userRole: UserRole | string | undefined): Permission[] {
  if (!userRole) return [];
  return ROLE_PERMISSIONS[userRole as UserRole] || [];
}

/**
 * Check if user is admin
 */
export function isAdmin(userRole: UserRole | string | undefined): boolean {
  return userRole === UserRole.ADMIN;
}

/**
 * Check if user is staff (not client or prospect)
 */
export function isStaff(userRole: UserRole | string | undefined): boolean {
  if (!userRole) return false;
  return STAFF_ROLES.includes(userRole as UserRole);
}